import { useRouter } from '@abyss/web/hooks/useRouter';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Layout } from '@abyss/web/ui/Layout';
import { Text } from '@abyss/web/ui/Text';
import { LineOfBusiness } from 'api/src/services/Constants';
import { useFlags } from 'launchdarkly-react-client-sdk';
import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import update from 'lodash/update';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConstantsLagoon } from '../../../common/ConstantsLagoon';
import {
  getGeoLocationFromStorage,
  setGeoLocationToStorage,
} from '../../../common/PSXHeader/SearchBar/utils';
import { useDeviceLocation } from '../../../hooks/useDeviceLocation';
import { useLagoon } from '../../../hooks/useLagoon';
import { useLocation } from '../../../hooks/useLocation';
import { refactorPlaceName } from '../../../utils/locationSearch.util';
import {
  FindUserLocationContainer,
  StyledLocationError,
  StyledLocationItems,
  StyledLocationSearch,
} from '../../ProviderSearch/context/SearchLocation/LocationInput.styled';
import { getLocationRequirement } from '../Guest.utils';
import { getBlueGreenGQLApiUrl } from '../utils';
import { GuestLocation } from './GuestLocation.utils';
import {
  StyledLocationHeader,
  StyledLocationSearchInstructionsText,
} from './GuestLocationInput.style';

export type PostalLocationData = {
  selectedLocation: GuestLocation;
  variables: { postal?: string; city?: string };
};

export type PostalPointData = {
  state?: string;
  county_id?: string;
  us_postal_point_id?: string;
  multicounty_flag?: string;
};

type GuestLocationSearchProps = {
  setLocation: (a: GuestLocation) => void;
  location?: GuestLocation;
  isLocationModal?: boolean;
  setIsOnFocus?: (a: boolean) => void;
  setCurrentInputValue?: (value: string) => void;
  onPostalLocationSelected?: (data: PostalLocationData) => void;
};

export type GeospatialCountyOption = {
  label: string;
  value: string;
  stateCode: string;
  countyId: string;
};

export type GeospatialSelectionPayload = {
  location: GuestLocation;
  countyOptions: GeospatialCountyOption[];
  isLocationModal: boolean;
};

export const GuestLocationInput = ({
  location,
  setLocation,
  isLocationModal,
  setIsOnFocus = () => {},
  setCurrentInputValue = () => {},
  onPostalLocationSelected,
}: GuestLocationSearchProps) => {
  const { t } = useTranslation();
  // Initialize inputVal from location prop on mount only
  const [inputVal, setInputVal] = useState(location?.label || '');
  const [isLoading, setIsLoading] = useState(false);
  // Track if we just made a selection to prevent onInputChange from clearing
  const [justSelected, setJustSelected] = useState(false);
  const lagoonMessagingTable = useLagoon('ui-messaging');
  const lagoonUIMessage =
    typeof lagoonMessagingTable === 'function'
      ? lagoonMessagingTable()
      : lagoonMessagingTable;
  const locationSearchInstructions = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.LOCATION_SEARCH_INSTRUCTIONS,
  })?.message;
  const locationSearchPlaceHolder =
    find(lagoonUIMessage, {
      key: ConstantsLagoon.GUEST_PLAN_SELECTION.LOCATION_SEARCH_PLACEHOLDER,
    })?.message || t('LOCATION_SEARCH.PLACEHOLDER_DECEMBER_RELEASE');
  const [gqlSuggestions, setgqlSuggestions] = useState<any[]>([
    {
      section: (
        <StyledLocationSearchInstructionsText data-testid="instructional-text">
          {locationSearchInstructions}
        </StyledLocationSearchInstructionsText>
      ),
      items: [
        {
          place_name: 'find',
          id: 'find',
        },
      ],
    },
  ]);
  const { getSearchParams } = useRouter();
  const [searchParams] = getSearchParams();
  const coverageType = searchParams?.get('coverageType') || '';
  const lob = searchParams?.get('planSelectionLob') || '';
  const normalizedLob = lob.toUpperCase();
  const isGeospatialPostalLob = [
    LineOfBusiness.MR,
    LineOfBusiness.CNS,
  ].includes(normalizedLob as LineOfBusiness);

  const flags = useFlags();
  const guestBGApiUrl = getBlueGreenGQLApiUrl(flags, searchParams);

  const [, getLocation] = useLocation({
    url: guestBGApiUrl,
    onCompleted: (result: {
      data: {
        location: {
          features: [
            {
              place_name: string;
              id: string;
              zipCode?: string;
              locationType?: string;
            }
          ];
        };
      };
    }) => {
      const resultSuggestions = cloneDeep(result?.data?.location?.features);
      resultSuggestions?.map((loc) => {
        const updatedName = refactorPlaceName(loc.place_name);
        update(loc, 'place_name', () => updatedName);
        update(loc, 'label', () => updatedName);
        return loc;
      });
      const findSuggestion =
        resultSuggestions?.length > 0 &&
        resultSuggestions.concat({
          place_name: 'find',
          id: 'find',
        });

      !isEmpty(resultSuggestions)
        ? setgqlSuggestions([
            {
              section: locationSearchInstructions,
              items: findSuggestion,
            },
          ])
        : setgqlSuggestions([
            {
              section: locationSearchInstructions,
              items: [],
            },
            {
              section: t('LOCATION_SEARCH.ERR_DEC_RELEASE'),
              items: [
                {
                  place_name: 'find',
                  id: 'find',
                },
              ],
            },
          ]);
    },
  });

  const { getDeviceLocation } = useDeviceLocation(setInputVal, setIsLoading);
  const geoLocation = getGeoLocationFromStorage();

  const locationRequirement = getLocationRequirement(lob, coverageType);
  const hasCountyRequirement = locationRequirement === 'county';

  const handleLocationChange = async (loc: string) => {
    await getLocation({
      variables: {
        address: encodeURIComponent(loc),
        countySearchEnabled: true,
        suppressRegions: hasCountyRequirement,
      },
    });
  };

  const debounceApiFilteringSearch = useCallback(
    debounce(async (inputValue: string, selectedItem: any) => {
      if (inputValue && !selectedItem && inputValue.trim().length) {
        await handleLocationChange(inputValue);
      }
      setIsLoading(false);
    }, 300),
    []
  );

  const handleClear = () => {
    setInputVal('');
    setCurrentInputValue('');
    setgqlSuggestions([
      {
        section: locationSearchInstructions,
        items: [
          {
            place_name: 'find',
            id: 'find',
          },
        ],
      },
    ]);
  };

  const handleFindMyLocationClick = () => {
    handleClear();
    getDeviceLocation();
  };

  /**
   * Handles changes to the location input field.
   * @param inputValue - The current value of the input field.
   * @param isArrowKeyDown - Whether the change was triggered by an arrow key event.
   */
  const handleLocationInputChange = (inputValue: any, isArrowKeyDown: any) => {
    // If we just made a selection and this is called with empty/selected value, ignore it
    if (justSelected) {
      setJustSelected(false);
      return;
    }

    if (inputValue?.trim().length) {
      if (isArrowKeyDown) {
        return;
      }
      setInputVal(inputValue);
      setCurrentInputValue(inputValue);
      setIsOnFocus(true);
      setIsLoading(true);
    } else {
      // Input is being cleared
      setInputVal('');
      setCurrentInputValue('');
      setgqlSuggestions([
        {
          section: locationSearchInstructions,
          items: [
            {
              place_name: 'find',
              id: 'find',
            },
          ],
        },
      ]);
      setIsOnFocus(true);
      setIsLoading(false);
    }
  };

  const [userLocationTriggered, setUserLocationTriggered] = useState(false);

  const handleSearch = async (_value: any, newLocation: any) => {
    if (
      !newLocation ||
      (typeof newLocation === 'object' && Object.keys(newLocation).length === 0)
    ) {
      handleClear();
      setIsOnFocus(false);
      setCurrentInputValue(''); // Notify parent that input is empty
      return;
    }

    // UNREACHABLE CODE: Already handled by the condition above - kept for reference
    if (!newLocation) {
      handleClear();
      setIsOnFocus(false);
      setLocation({
        latitude: undefined as unknown as number,
        longitude: undefined as unknown as number,
        label: null,
        zipcode: undefined,
        stateCode: undefined,
        locationType: undefined,
      } as unknown as GuestLocation);
      setGeoLocationToStorage({});
      return;
    }
    if (newLocation.place_name === 'find') {
      setUserLocationTriggered(true);
      return;
    }

    const selectedName = newLocation.place_name;
    setJustSelected(true); // Prevent next onInputChange from clearing
    setInputVal(selectedName);
    setCurrentInputValue(selectedName);

    const selectedLocation = {
      latitude: newLocation.center[1],
      longitude: newLocation.center[0],
      label: selectedName,
      zipcode: newLocation.zipCode,
      stateCode: newLocation.stateCode,
      locationType: newLocation.locationType,
    } as GuestLocation;

    const {
      id: locationId,
      center = [],
      place_name: placeName,
      stateCode: targetStateCode,
      zipCode: targetZipCode,
    } = newLocation;
    const [selectedLong, selectedLat] = center;
    setGeoLocationToStorage({
      id: locationId,
      name: placeName,
      longitude: selectedLong,
      latitude: selectedLat,
      stateCode: targetStateCode,
      zipCode: targetZipCode,
    });

    const isPostcodeSelection =
      isGeospatialPostalLob &&
      newLocation?.locationType?.toLowerCase() === 'postcode';
    const isPlacecodeSelection =
      normalizedLob === LineOfBusiness.MR &&
      newLocation?.locationType?.toLowerCase() === 'place';
    const shouldUsePostalPointApi = isPostcodeSelection || isPlacecodeSelection;
    if (shouldUsePostalPointApi) {
      const variables = isPostcodeSelection
        ? { postal: String(newLocation.zipCode) }
        : {
            city: String(newLocation.place_name.split(',')[0])
              ?.toUpperCase()
              .trim(),
          };

      // Notify parent to handle postal point API call
      if (onPostalLocationSelected) {
        onPostalLocationSelected({
          selectedLocation,
          variables,
        });
        setIsOnFocus(false);
        return;
      }
    }

    // Non-postal location: set directly
    setLocation(selectedLocation);

    setIsOnFocus(false);
  };

  useEffect(() => {
    if (
      userLocationTriggered &&
      geoLocation?.name != null &&
      geoLocation?.name !== location?.label
    ) {
      setInputVal(geoLocation.name);
      setCurrentInputValue(geoLocation.name);
      setLocation({
        latitude: geoLocation.latitude,
        longitude: geoLocation.longitude,
        label: geoLocation.name,
        zipcode: geoLocation.zipCode,
        stateCode: geoLocation.stateCode,
        locationType: 'address',
      });
      setIsOnFocus(false);
      setUserLocationTriggered(false);
    }
  }, [geoLocation]);

  return (
    <StyledLocationSearch
      apiFiltering={debounceApiFilteringSearch}
      css={{
        '&.abyss-search-input-root': {
          border: '0px',
        },
      }}
      customRender={(suggestion: any) =>
        suggestion.section ? (
          suggestion.section !== t('LOCATION_SEARCH.ERR_DEC_RELEASE') &&
          suggestion.section !== t('LOCATION_SEARCH.ERROR') ? (
            <StyledLocationHeader>{suggestion.section}</StyledLocationHeader>
          ) : (
            <StyledLocationError alignItems="top">
              <IconMaterial color="$error1" icon="error" size="$md" />
              <Text color="$error1" size="$md">
                {suggestion.section}
              </Text>
            </StyledLocationError>
          )
        ) : suggestion.place_name !== 'find' ? (
          <StyledLocationItems grow space={0}>
            <Text color="$accent1" fontWeight="$bold" size="$md">
              {suggestion.place_name}
            </Text>
          </StyledLocationItems>
        ) : (
          <StyledLocationItems>
            <FindUserLocationContainer
              data-auto-testid="find-user-location-clickable-element"
              data-testid="find-user-location-clickable-element"
              onClick={handleFindMyLocationClick}
            >
              <Layout.Group space={4}>
                <IconMaterial
                  css={{ marginRight: '10px' }}
                  icon="my_location"
                />
                <Text color="$interactive1" fontWeight="$semibold" size="md">
                  {t('Find my location')}
                </Text>
              </Layout.Group>
            </FindUserLocationContainer>
          </StyledLocationItems>
        )
      }
      data-auto-testid="styled-location-search"
      data-testid="styled-location-search"
      hideLabel
      inputDefaultValue={inputVal}
      inputLeftElement={
        <IconSymbol
          color={isLocationModal ? '$gray5' : '$gray8'}
          icon="location_on"
          size={24}
        />
      }
      isLoading={isLoading}
      isLocationModal={isLocationModal}
      keys={['value', 'location', 'place_name']}
      label={t('Location')}
      onBlur={() => {
        setIsOnFocus(false);
      }}
      onChange={handleSearch}
      onClear={handleClear}
      onFocus={() => {
        if (isLocationModal) {
          setIsOnFocus(true);
        }
      }}
      onInputChange={handleLocationInputChange}
      openOnFocus={!isLocationModal}
      options={gqlSuggestions}
      placeholder={inputVal || locationSearchPlaceHolder}
      rounded
      value={inputVal}
    />
  );
};
